import { createAuthClient } from "better-auth/react";

export type AuthUser = {
  email: string;
  emailVerified: boolean;
  id: string;
  image: string | null;
  name: string;
};

type AuthSessionData = {
  session?: {
    id?: string | null;
  } | null;
  user?: {
    email?: string | null;
    emailVerified?: boolean | null;
    id?: string | null;
    image?: string | null;
    name?: string | null;
  } | null;
} | null;
type AuthSessionUser = NonNullable<AuthSessionData>["user"];

type AuthActionResponse<T = unknown> = {
  data?: T | null;
  error?: {
    code?: string;
    message?: string;
    status?: number;
  } | null;
};

export const authClient = createAuthClient({
  basePath: "/api/auth",
});

function normalizeUser(user: AuthSessionUser): AuthUser | null {
  if (!user?.id) {
    return null;
  }

  return {
    email: user.email ?? "",
    emailVerified: user.emailVerified === true,
    id: user.id,
    image: user.image ?? null,
    name: user.name?.trim() || user.email || "Govoptes user",
  };
}

function getAuthErrorMessage(error: AuthActionResponse["error"], fallback: string): string {
  return error?.message?.trim() || fallback;
}

async function unwrapAuthAction<T>(promise: Promise<AuthActionResponse<T>>, fallback: string): Promise<T | null> {
  const response = await promise;
  if (response.error) {
    throw new Error(getAuthErrorMessage(response.error, fallback));
  }
  return response.data ?? null;
}

export function useAuthSession() {
  const session = authClient.useSession();
  const data = session.data as AuthSessionData;
  const user = normalizeUser(data?.user ?? null);

  return {
    error: session.error,
    isLoaded: !session.isPending,
    isRefetching: session.isRefetching,
    isSignedIn: Boolean(user),
    refetch: session.refetch,
    sessionId: data?.session?.id ?? null,
    user,
  };
}

export function useAuth() {
  const session = useAuthSession();

  return {
    isLoaded: session.isLoaded,
    isSignedIn: session.isSignedIn,
    sessionId: session.sessionId,
    signOut,
    userId: session.user?.id ?? null,
  };
}

export function useUser() {
  const session = useAuthSession();

  return {
    isLoaded: session.isLoaded,
    isSignedIn: session.isSignedIn,
    user: session.user,
  };
}

export async function signInWithEmail(input: { email: string; password: string }) {
  return unwrapAuthAction(
    authClient.signIn.email({
      email: input.email,
      password: input.password,
    }) as Promise<AuthActionResponse>,
    "Unable to sign in with that email and password.",
  );
}

export async function signUpWithEmail(input: { email: string; name: string; password: string }) {
  return unwrapAuthAction(
    authClient.signUp.email({
      email: input.email,
      name: input.name,
      password: input.password,
    }) as Promise<AuthActionResponse>,
    "Unable to create that account.",
  );
}

export async function requestPasswordReset(input: { email: string; redirectTo: string }) {
  return unwrapAuthAction(
    authClient.requestPasswordReset({
      email: input.email,
      redirectTo: input.redirectTo,
    }) as Promise<AuthActionResponse>,
    "Unable to send a password reset email.",
  );
}

export async function resetPassword(input: { newPassword: string; token: string }) {
  return unwrapAuthAction(
    authClient.resetPassword({
      newPassword: input.newPassword,
      token: input.token,
    }) as Promise<AuthActionResponse>,
    "Unable to reset that password.",
  );
}

export async function updateUserProfile(input: { name: string }) {
  return unwrapAuthAction(
    authClient.updateUser({
      name: input.name,
    }) as Promise<AuthActionResponse>,
    "Unable to update your profile.",
  );
}

export async function changeEmail(input: { callbackURL: string; newEmail: string }) {
  return unwrapAuthAction(
    authClient.changeEmail({
      callbackURL: input.callbackURL,
      newEmail: input.newEmail,
    }) as Promise<AuthActionResponse>,
    "Unable to start that email change.",
  );
}

export async function changePassword(input: { currentPassword: string; newPassword: string; revokeOtherSessions?: boolean }) {
  return unwrapAuthAction(
    authClient.changePassword({
      currentPassword: input.currentPassword,
      newPassword: input.newPassword,
      revokeOtherSessions: input.revokeOtherSessions,
    }) as Promise<AuthActionResponse>,
    "Unable to update your password.",
  );
}

export async function signOut() {
  return unwrapAuthAction(authClient.signOut() as Promise<AuthActionResponse>, "Unable to sign out.");
}
